import {
  DataRef,
  DndContext,
  DragEndEvent,
  MouseSensor,
  useSensor,
} from "@dnd-kit/core";
import { ReactNode } from "react";
import { useProjectTablesTableContext } from "./provider/ProjectTablesTableProvider";
import { ProjectTablesDragOverlay } from "./ProjectTablesDragOverlay";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { captureException } from "@sentry/react";
import { PROJECT_TABLES_REMOVE_FROM_FOLDER_DROPPABLE_ID } from "./ProjectTablesTableLayout";

export type ProjectTablesDraggableData = {
  tableId: string;
};

export type ProjectTablesDroppableData = {
  folderId: string;
};

export const ProjectTablesDndProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      // Mouse must move at least 1 pixel before a drag start event is emitted
      // This allows click events to be detected
      distance: 1,
    },
  });

  const moveTableToFolder = useProjectTablesTableContext(
    (s) => s.moveTableToFolder,
  );

  const tables = useDataTableContext((s) => s.tables);

  const hasPermission = useProjectPermissionStore((s) => s.permissions.edit);
  const projectId = useDataTableContext((s) => s.project.id);

  const handleDragEnd = (e: DragEndEvent) => {
    if (!hasPermission) {
      addUtilityToastFailure("Permission denied");
      captureException("Folder drop event captured on unpermitted project", {
        extra: { projectId },
      });
      return;
    }
    const tableId = (e.active.data as DataRef<ProjectTablesDraggableData>)
      .current?.tableId;

    const table = tables.find((table) => tableId === table.id);

    const folderId = (
      e.over?.data as DataRef<ProjectTablesDroppableData> | undefined
    )?.current?.folderId;

    if (table !== undefined && folderId !== undefined) {
      moveTableToFolder(table, folderId);
    }

    const droppableId = e.over?.id;
    if (
      table !== undefined &&
      droppableId === PROJECT_TABLES_REMOVE_FROM_FOLDER_DROPPABLE_ID
    ) {
      moveTableToFolder(table, null);
    }
  };

  return (
    <DndContext sensors={[mouseSensor]} onDragEnd={handleDragEnd}>
      {children}
      <ProjectTablesDragOverlay />
    </DndContext>
  );
};
